import { api } from '../services/axios';
import { getApiBaseUrl } from '../config/environment';
import { authService } from './auth';
import type { Anexo } from '../models';

class AnexoService {
  /**
   * Deletar um anexo
   */
  async delete(anexoId: number): Promise<void> {
    // Garantir que Sanctum esteja inicializado
    await authService.ensureSanctumInitialized();
    
    await api.delete(`/anexos/${anexoId}`);
  }

  /**
   * Obter URL de download do anexo
   */
  getDownloadUrl(anexo: Anexo): string {
    // Se a URL já é absoluta, retornar como está
    if (anexo.anexo.startsWith('http')) {
      return anexo.anexo;
    }

    // A API expõe uma rota protegida: GET /api/storage/anexos/{filename}
    // O campo anexo geralmente vem como "anexos/<arquivo.ext>", então precisamos do basename
  const filename = encodeURIComponent(anexo.anexo.split('/').pop() || anexo.anexo);
  const baseApi = getApiBaseUrl();
  return `${baseApi}/storage/anexos/${filename}`;
  }

  /**
   * Obter URL de visualização do anexo
   */
  getViewUrl(anexo: Anexo): string {
    return this.getDownloadUrl(anexo);
  }

  /**
   * Verificar se o arquivo é uma imagem
   */
  isImage(anexo: Anexo): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const extension = anexo.nome_anexo.split('.').pop()?.toLowerCase() || '';
    return imageExtensions.includes(extension);
  }

  /**
   * Verificar se o arquivo pode ser visualizado no navegador
   */
  canPreview(anexo: Anexo): boolean {
    const previewExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'pdf', 'txt'];
    const extension = anexo.nome_anexo.split('.').pop()?.toLowerCase() || '';
    return previewExtensions.includes(extension);
  }

  /**
   * Baixar anexo como blob com autenticação
   */
  async downloadAsBlob(anexo: Anexo): Promise<Blob> {
    // Garantir que Sanctum esteja inicializado
    await authService.ensureSanctumInitialized();
    
    // Montar endpoint correto para a rota protegida
    if (anexo.anexo.startsWith('http')) {
      // Para URLs absolutas, ainda tentamos baixar via axios para preservar headers quando possível
      const response = await api.get(anexo.anexo, {
        responseType: 'blob',
        headers: { 'Accept': '*/*' }
      });
      return response.data;
    }

    const filename = encodeURIComponent(anexo.anexo.split('/').pop() || anexo.anexo);
    const response = await api.get(`/storage/anexos/${filename}`, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*'
      }
    });
    
    return response.data;
  }

  /**
   * Obter URL temporária para visualização do anexo
   */
  async viewAsBlob(anexo: Anexo): Promise<string> {
    const blob = await this.downloadAsBlob(anexo);
    return URL.createObjectURL(blob);
  }

  /**
   * Baixar anexo programaticamente
   */
  async download(anexo: Anexo): Promise<void> {
    let link: HTMLAnchorElement | null = null;
    let downloadUrl: string | null = null;

    try {
      // Usar o método downloadAsBlob para manter autenticação
      const blob = await this.downloadAsBlob(anexo);
      downloadUrl = URL.createObjectURL(blob);
      
      link = document.createElement('a');
      link.href = downloadUrl;
      link.download = anexo.nome_anexo;
      document.body.appendChild(link);
      link.click();
      
      // Aguardar um pouco antes de remover para garantir que o download iniciou
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
      
      // Fallback: tentar método antigo
      const url = this.getDownloadUrl(anexo);
      window.open(url, '_blank');
    } finally {
      // Cleanup seguro: remove() não lança erro se o elemento não estiver no DOM
      link?.remove();
      
      // Limpar o objeto URL
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    }
  }
}

export const anexoService = new AnexoService();
